import React, { useState, useEffect, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { apiService, handleApiError } from '../services/api';
import { useMeeting } from '../contexts/MeetingContext';
import {
  MagnifyingGlassIcon,
  ChevronDownIcon,
  ChevronRightIcon,
  DocumentTextIcon,
  TagIcon,
  ExclamationTriangleIcon,
  BeakerIcon,
  LightBulbIcon,
  EyeIcon,
  ClipboardDocumentIcon,
  ArrowDownTrayIcon,
  StarIcon,
  FunnelIcon,
} from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import PDFDocumentViewer from './PDFDocumentViewer';
import jsPDF from 'jspdf';
import 'jspdf-autotable';

const FrequencyBadge = ({ category, count }) => {
  const getBadgeClass = (category) => {
    switch (category?.toLowerCase()) {
      case 'common': return 'badge-common';
      case 'intermediate': return 'badge-intermediate';
      case 'rare': return 'badge-rare';
      default: return 'badge-proposal';
    }
  };

  return (
    <span className={getBadgeClass(category)}>
      {category || 'Unknown'}
    </span>
  );
};

const CompanyPill = ({ company, highlighted = false, onClick }) => {
  if (!company) return null;
  
  // Shorten company names for display
  const getDisplayName = (company) => {
    // Handle common long names
    const shortNames = {
      'ZTE Corporation, Sanechips': 'ZTE',
      'Huawei, HiSilicon': 'Huawei',
      'Spreadtrum, UNISOC': 'UNISOC',
      'LG Electronics Inc.': 'LG',
      'NTT DOCOMO, INC.': 'NTT DOCOMO',
      'Qualcomm Incorporated': 'Qualcomm',
      'Google Ireland Limited': 'Google',
      'Transsion Holdings': 'Transsion',
      'InterDigital Inc.': 'InterDigital'
    };
    
    return shortNames[company] || company;
  };

  const handleClick = (e) => {
    e.stopPropagation(); // Prevent event bubbling
    if (onClick) {
      onClick(company);
    }
  };

  return (
    <span 
      onClick={handleClick}
      className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ml-2 transition-all cursor-pointer ${
        highlighted 
          ? 'bg-vodafone-500 text-white ring-2 ring-vodafone-300 shadow-lg scale-110' 
          : 'bg-green-100 text-green-800 hover:bg-green-200 hover:shadow-md'
      }`}
      title={`Click to ${highlighted ? 'clear' : 'filter by'} ${company}`}
    >
      {getDisplayName(company)}
    </span>
  );
};

const SemanticGroupCard = ({ group, isExpanded, onToggle, handleViewPDF, subtopicName, companyFilter, onCompanyClick }) => {
  const getBorderClass = (category) => {
    switch (category?.toLowerCase()) {
      case 'common': return 'border-l-green-500';
      case 'intermediate': return 'border-l-orange-500';
      case 'rare': return 'border-l-purple-500';
      default: return 'border-l-gray-300';
    }
  };

  // Extract unique companies from all proposals in this semantic group
  const getUniqueCompanies = (proposals) => {
    const companies = new Set();
    proposals?.forEach(proposal => {
      if (proposal.company) {
        companies.add(proposal.company);
      }
    });
    return Array.from(companies).sort();
  };

  const uniqueCompanies = getUniqueCompanies(group.verbatim_proposals);


  return (
    <div className={`card-hover border-l-4 ${getBorderClass(group.frequency_category)}`}>
      <div className="space-y-4">
        {/* Header */}
        <div className="flex items-start justify-between">
          <div className="flex-1">
            <div className="flex items-center justify-between mb-3">
              <h4 className="text-sm font-semibold text-charcoal-900">
                {group.group_id?.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()) || 'Semantic Group'}
              </h4>
              <span className="badge-proposal">
                {group.frequency_category?.toUpperCase() || 'UNKNOWN'}
              </span>
            </div>
            
            <div className="bg-blue-50 border-l-4 border-blue-400 p-3 rounded-r">
              <h5 className="text-xs font-medium text-blue-900 mb-1 uppercase tracking-wide flex items-center">
                <LightBulbIcon className="h-3 w-3 mr-1" />
                Position:
              </h5>
              <p className="text-sm text-blue-800 leading-relaxed">
                {group.technical_concept || 'No position available'}
              </p>
            </div>

            {/* Company Pills for this Semantic Group */}
            {uniqueCompanies.length > 0 && (
              <div className="mt-3">
                <h6 className="text-xs font-medium text-gray-600 mb-2 uppercase tracking-wide">
                  Contributing Companies ({uniqueCompanies.length}):
                </h6>
                <div className="flex flex-wrap gap-1">
                  {uniqueCompanies.map((company, index) => (
                    <CompanyPill 
                      key={index} 
                      company={company} 
                      highlighted={companyFilter && company === companyFilter}
                      onClick={onCompanyClick}
                    />
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Expandable Proposals Section */}
        <div className="border-t border-gray-200 pt-3">
          <button
            onClick={() => onToggle(group.group_id)}
            className="flex items-center space-x-2 text-sm text-charcoal-600 hover:text-charcoal-900 transition-colors"
          >
            {isExpanded ? (
              <ChevronDownIcon className="h-4 w-4" />
            ) : (
              <ChevronRightIcon className="h-4 w-4" />
            )}
            <span>Documents ({group.document_count || 0})</span>
          </button>

          {isExpanded && (
            <div className="mt-3 space-y-4 animate-slideUp">
              {(() => {
                // Group proposals by stance (handle both "support"/"supporting" formats)
                const supportingProposals = group.verbatim_proposals?.filter(p => 
                  p.stance === 'supporting' || p.stance === 'support'
                ) || [];
                const neutralProposals = group.verbatim_proposals?.filter(p => p.stance === 'neutral') || [];
                const opposingProposals = group.verbatim_proposals?.filter(p => 
                  p.stance === 'opposing' || p.stance === 'oppose'
                ) || [];
                const otherProposals = group.verbatim_proposals?.filter(p => !p.stance) || [];
                
                if (!group.verbatim_proposals || group.verbatim_proposals.length === 0) {
                  return (
                    <div className="text-center py-6">
                      <DocumentTextIcon className="h-8 w-8 text-gray-400 mx-auto mb-2" />
                      <p className="text-sm text-charcoal-500 italic">No verbatim proposals available</p>
                    </div>
                  );
                }

                const StanceSection = ({ title, proposals, bgColor, borderColor, textColor, icon }) => {
                  if (proposals.length === 0) return null;
                  
                  return (
                    <div className={`border-2 ${borderColor} rounded-lg p-4 ${bgColor}`}>
                      <div className="flex items-center space-x-2 mb-3">
                        <span className="text-base font-semibold flex items-center space-x-2">
                          <span className={textColor}>{icon}</span>
                          <span className={textColor}>{title}</span>
                        </span>
                        <span className="text-xs bg-white px-2 py-1 rounded border border-gray-200">
                          {proposals.length} proposal{proposals.length !== 1 ? 's' : ''}
                        </span>
                      </div>
                      <div className="space-y-3">
                        {proposals.map((proposal, index) => (
                          <div key={index} className="bg-white border border-gray-200 rounded-lg p-3">
                            <div className="flex items-start space-x-3">
                              <DocumentTextIcon className="h-5 w-5 text-vodafone-600 mt-1 flex-shrink-0" />
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center justify-between mb-2">
                                  <div className="flex items-center space-x-2">
                                    <span className="text-sm font-semibold text-vodafone-700">
                                      {proposal.document}
                                    </span>
                                    <CompanyPill 
                                      company={proposal.company} 
                                      highlighted={companyFilter && proposal.company === companyFilter}
                                      onClick={onCompanyClick}
                                    />
                                  </div>
                                  <button
                                    onClick={() => handleViewPDF(proposal.document, proposal.page, group.group_id, subtopicName)}
                                    className="flex-shrink-0 p-1.5 text-gray-500 hover:text-vodafone-600 hover:bg-gray-100 rounded transition-colors"
                                    title="View in PDF"
                                  >
                                    <EyeIcon className="h-4 w-4" />
                                  </button>
                                </div>
                                <blockquote className="text-sm text-charcoal-800 leading-relaxed border-l-2 border-gray-300 pl-3 mb-2">
                                  {proposal.proposal_text || proposal.supporting_quote || 'No proposal text available'}
                                </blockquote>
                                {proposal.stance_rationale && (
                                  <div className="mt-2 text-xs text-gray-600 italic bg-gray-50 p-2 rounded">
                                    <span className="font-medium">Rationale:</span> {proposal.stance_rationale}
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                };

                return (
                  <>
                    <StanceSection
                      title="Supporting Proposals"
                      proposals={supportingProposals}
                      bgColor="bg-green-50"
                      borderColor="border-green-300"
                      textColor="text-green-700"
                      icon="✓"
                    />
                    <StanceSection
                      title="Neutral Proposals"
                      proposals={neutralProposals}
                      bgColor="bg-gray-50"
                      borderColor="border-gray-300"
                      textColor="text-gray-700"
                      icon="○"
                    />
                    <StanceSection
                      title="Opposing Proposals"
                      proposals={opposingProposals}
                      bgColor="bg-red-50"
                      borderColor="border-red-300"
                      textColor="text-red-700"
                      icon="✗"
                    />
                    {otherProposals.length > 0 && (
                      <StanceSection
                        title="Other Proposals"
                        proposals={otherProposals}
                        bgColor="bg-blue-50"
                        borderColor="border-blue-200"
                        textColor="text-blue-700"
                        icon="•"
                      />
                    )}
                  </>
                );
              })()}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const StrategicBriefSidebar = ({ selectedSubtopic, loading }) => {
  const [strategicBrief, setStrategicBrief] = useState('');
  const [briefLoading, setBriefLoading] = useState(false);
  const [briefExists, setBriefExists] = useState(false);

  const loadStrategicBrief = useCallback(async () => {
    if (!selectedSubtopic?.id) {
      setStrategicBrief('');
      setBriefExists(false);
      return;
    }

    try {
      setBriefLoading(true);
      const response = await apiService.getAISummary(selectedSubtopic.id);
      setStrategicBrief(response.data.summary);
      setBriefExists(true);
    } catch (error) {
      console.log('No existing executive summary found');
      setStrategicBrief('');
      setBriefExists(false);
    } finally {
      setBriefLoading(false);
    }
  }, [selectedSubtopic?.id]);


  const copyToClipboard = async () => {
    if (!strategicBrief) return;
    
    try {
      // Create clean text version (remove HTML formatting)
      const cleanText = strategicBrief
        .replace(/<br\s*\/?>/gi, '\n')
        .replace(/<strong[^>]*>(.*?)<\/strong>/gi, '$1')
        .replace(/<[^>]*>/g, '')
        .trim();
      
      await navigator.clipboard.writeText(cleanText);
      toast.success('Executive summary copied to clipboard!');
    } catch (error) {
      console.error('Failed to copy to clipboard:', error);
      toast.error('Failed to copy to clipboard');
    }
  };

  const downloadAsPDF = async () => {
    if (!strategicBrief || !selectedSubtopic) return;
    
    try {
      // Create clean text version
      const cleanText = strategicBrief
        .replace(/<br\s*\/?>/gi, '\n')
        .replace(/<strong[^>]*>(.*?)<\/strong>/gi, '$1')
        .replace(/<[^>]*>/g, '')
        .trim();
      
      // Create a simple text file for now (PDF generation would require additional libraries)
      const blob = new Blob([
        `Executive Summary: ${selectedSubtopic.subtopic_name || selectedSubtopic.name}\n\n${cleanText}`
      ], { type: 'text/plain' });
      
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `executive-summary-${(selectedSubtopic.subtopic_name || selectedSubtopic.name).replace(/[^a-zA-Z0-9]/g, '-').toLowerCase()}.txt`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      
      toast.success('Executive summary downloaded!');
    } catch (error) {
      console.error('Failed to download:', error);
      toast.error('Failed to download executive summary');
    }
  };

  useEffect(() => {
    loadStrategicBrief();
  }, [loadStrategicBrief]);

  const formatBrief = (text) => {
    if (!text || typeof text !== 'string') return '';
    
    // Split into lines for better processing
    const lines = text.split('\n');
    let formatted = '';
    let currentSection = '';
    let sectionContent = [];
    
    // Process lines to group by sections
    for (let line of lines) {
      if (!line.trim()) continue;
      
      // Handle section headers in format: **Section Name**: or **Section Name**:
      // This matches: **Overview**, **Key Debates & Vendor Positions**, **Areas of Consensus**, etc.
      const sectionHeaderMatch = line.trim().match(/^\*\*([^*]+)\*\*:?\s*(.*)/);
      
      if (sectionHeaderMatch) {
        // Process previous section if exists
        if (currentSection && sectionContent.length > 0) {
          formatted += formatSection(currentSection, sectionContent);
        }
        
        // Start new section
        currentSection = sectionHeaderMatch[1].trim();
        sectionContent = [];
        
        // If there's content on the same line after the header, add it
        if (sectionHeaderMatch[2] && sectionHeaderMatch[2].trim()) {
          sectionContent.push(sectionHeaderMatch[2].trim());
        }
      }
      // Handle old-style section headers with bullet: • **Section Name**
      else if (line.trim().startsWith('• **')) {
        // Process previous section if exists
        if (currentSection && sectionContent.length > 0) {
          formatted += formatSection(currentSection, sectionContent);
        }
        
        // Extract section name
        const oldStyleMatch = line.match(/• \*\*([^*]+)\*\*/);
        if (oldStyleMatch) {
          currentSection = oldStyleMatch[1].trim();
          sectionContent = [];
        }
      }
      // Handle bullet points (- )
      else if (line.trim().startsWith('- ')) {
        const content = line.trim().substring(2);
        sectionContent.push(content);
      }
      // Handle regular content lines (not bullets or headers)
      else if (currentSection && line.trim() && !line.trim().startsWith('•')) {
        sectionContent.push(line.trim());
      }
    }
    
    // Process final section
    if (currentSection && sectionContent.length > 0) {
      formatted += formatSection(currentSection, sectionContent);
    }
    
    return formatted;
  };

  const formatSection = (sectionName, content) => {
    let bgColor = 'bg-gray-50';
    let borderColor = 'border-gray-300';
    let textColor = 'text-gray-900';
    let iconSvg = '';
    let displayName = sectionName;
    
    // Assign colors and icons based on section name
    if (sectionName.toLowerCase().includes('overview')) {
      bgColor = 'bg-blue-50';
      borderColor = 'border-blue-300';
      textColor = 'text-blue-900';
      iconSvg = '<svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"></path></svg>';
    } 
    else if (sectionName.toLowerCase().includes('debate') || sectionName.toLowerCase().includes('position')) {
      bgColor = 'bg-orange-50';
      borderColor = 'border-orange-300';
      textColor = 'text-orange-900';
      iconSvg = '<svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 10V3L4 14h7v7l9-11h-7z"></path></svg>';
    }
    else if (sectionName.toLowerCase().includes('consensus')) {
      bgColor = 'bg-green-50';
      borderColor = 'border-green-300';
      textColor = 'text-green-900';
      iconSvg = '<svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"></path></svg>';
    }
    else if (sectionName.toLowerCase().includes('insight') || sectionName.toLowerCase().includes('strategic')) {
      bgColor = 'bg-purple-50';
      borderColor = 'border-purple-300';
      textColor = 'text-purple-900';
      iconSvg = '<svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z"></path></svg>';
    }
    // Legacy support for Common/Intermediate/Rare
    else if (sectionName.toLowerCase().includes('common')) {
      bgColor = 'bg-green-50';
      borderColor = 'border-green-300';
      textColor = 'text-green-900';
      displayName = 'Common Positions';
      iconSvg = '<svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"></path></svg>';
    } 
    else if (sectionName.toLowerCase().includes('intermediate')) {
      bgColor = 'bg-orange-50';
      borderColor = 'border-orange-300';
      textColor = 'text-orange-900';
      displayName = 'Intermediate Positions';
      iconSvg = '<svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path></svg>';
    } 
    else if (sectionName.toLowerCase().includes('rare')) {
      bgColor = 'bg-purple-50';
      borderColor = 'border-purple-300';
      textColor = 'text-purple-900';
      displayName = 'Rare Positions';
      iconSvg = '<svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z"></path></svg>';
    }
    
    let sectionHtml = `<div class="mb-4 p-4 ${bgColor} rounded-lg border ${borderColor}">
      <h4 class="font-semibold ${textColor} mb-3 text-base flex items-center gap-2">
        <span class="${textColor}">${iconSvg}</span>
        ${displayName}
      </h4>`;
    
    // Check if this is an overview-style section (paragraph text vs bullet points)
    const isOverviewStyle = sectionName.toLowerCase().includes('overview');
    
    if (isOverviewStyle) {
      // Display as paragraph text
      const overviewText = content.join(' ');
      let formattedContent = overviewText.replace(/\*\*([^*]+)\*\*/g, '<strong class="text-charcoal-800">$1</strong>');
      
      sectionHtml += `<div class="text-sm text-gray-700 leading-relaxed">
        ${formattedContent}
      </div>`;
    } else {
      // Handle sections with bullet points or structured content
      content.forEach(item => {
        // Handle bold text
        let formattedContent = item.replace(/\*\*([^*]+)\*\*/g, '<strong class="text-charcoal-800">$1</strong>');
        
        // Handle document citations - split multiple citations and create separate bubbles
        formattedContent = formattedContent.replace(/\(([^)]+)\)/g, (match, citations) => {
          // Check if this looks like document references (R2-, R3-, S2-, etc.)
          const docs = citations.split(',').map(doc => doc.trim()).filter(doc => /^[RS]\d+-/.test(doc));
          if (docs.length > 0) {
            return docs.map(doc => 
              `<span class="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800 ml-1">${doc}</span>`
            ).join('');
          }
          return match; // Return original if no document references found
        });
        
        sectionHtml += `<div class="mb-3 flex items-start ml-4">
          <span class="inline-block w-2 h-2 bg-gray-400 rounded-full mt-2 mr-3 flex-shrink-0"></span>
          <span class="text-sm text-gray-700 leading-relaxed">${formattedContent}</span>
        </div>`;
      });
    }
    
    sectionHtml += '</div>';
    return sectionHtml;
  };

  if (!selectedSubtopic) {
    return (
      <div className="card">
        <h3 className="text-lg font-semibold text-charcoal-900 mb-4">Executive Summary</h3>
        <p className="text-gray-500 text-sm">Select a subtopic to view executive summary</p>
      </div>
    );
  }

  return (
    <div className="card">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-charcoal-900">Executive Summary</h3>
        <div className="flex items-center space-x-2">
          {briefExists && strategicBrief && (
            <>
              <button
                onClick={copyToClipboard}
                disabled={briefLoading}
                className="p-1.5 text-gray-600 hover:text-vodafone-600 hover:bg-vodafone-50 rounded transition-colors"
                title="Copy to clipboard"
              >
                <ClipboardDocumentIcon className="h-4 w-4" />
              </button>
              <button
                onClick={downloadAsPDF}
                disabled={briefLoading}
                className="p-1.5 text-gray-600 hover:text-vodafone-600 hover:bg-vodafone-50 rounded transition-colors"
                title="Download as text file"
              >
                <ArrowDownTrayIcon className="h-4 w-4" />
              </button>
            </>
          )}
        </div>
      </div>

      {briefLoading ? (
        <div className="text-center py-8">
          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-vodafone-500 mx-auto mb-3"></div>
          <p className="text-sm text-gray-600">Loading executive summary...</p>
        </div>
      ) : briefExists && strategicBrief ? (
        <div 
          className="text-sm text-charcoal-700 leading-relaxed space-y-3"
          dangerouslySetInnerHTML={{ __html: formatBrief(strategicBrief) }}
        />
      ) : (
        <div className="text-center py-8">
          <LightBulbIcon className="h-8 w-8 text-gray-400 mx-auto mb-3" />
          <p className="text-sm text-gray-600">No executive summary available</p>
        </div>
      )}
    </div>
  );
};

const SubtopicSidebar = ({ subtopics, selectedSubtopic, onSelectSubtopic, loading }) => {
  const filteredSubtopics = subtopics;

  return (
    <div className="bg-white rounded-xl shadow-card border border-gray-200">
      <div className="p-6 border-b border-gray-200">
        <h2 className="text-lg font-semibold text-charcoal-900">Subtopics</h2>
      </div>

      <div className="p-4">
        {loading ? (
          <div className="space-y-3">
            {[1, 2, 3, 4].map((i) => (
              <div key={i} className="animate-pulse">
                <div className="h-4 bg-gray-200 rounded w-3/4 mb-2"></div>
                <div className="h-3 bg-gray-200 rounded w-1/2"></div>
              </div>
            ))}
          </div>
        ) : filteredSubtopics.length > 0 ? (
          <div className="space-y-2">
            {filteredSubtopics.map((subtopic) => (
              <button
                key={subtopic.id || subtopic.subtopic_name}
                onClick={() => onSelectSubtopic(subtopic)}
                className={`w-full text-left p-3 rounded-lg transition-colors ${
                  selectedSubtopic?.id === subtopic.id
                    ? 'bg-vodafone-50 border-l-4 border-vodafone-500'
                    : 'hover:bg-gray-50'
                }`}
              >
                <div className="space-y-2">
                  <div className="flex items-center gap-2">
                    <h3 className="text-sm font-medium text-charcoal-900 line-clamp-2 flex-1">
                      {subtopic.name || subtopic.subtopic_name || 'Unnamed Subtopic'}
                    </h3>
                    {(subtopic.name || subtopic.subtopic_name)?.includes('Overview') && (
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-[10px] font-semibold bg-gradient-to-r from-vodafone-500 to-vodafone-600 text-white tracking-wide uppercase shadow-sm flex-shrink-0">
                        Overview
                      </span>
                    )}
                  </div>
                  <div className="text-xs text-charcoal-600">
                    <span>{subtopic.total_proposals || 0} proposals • {subtopic.document_count || 0} documents</span>
                  </div>
                </div>
              </button>
            ))}
          </div>
        ) : (
          <div className="text-center py-6">
            <MagnifyingGlassIcon className="h-8 w-8 text-gray-400 mx-auto mb-2" />
            <p className="text-sm text-charcoal-500">No subtopics found</p>
          </div>
        )}
      </div>
    </div>
  );
};

const SubtopicBrowser = () => {
  const location = useLocation();
  const { selectedMeeting, selectedAgenda } = useMeeting();
  const [subtopics, setSubtopics] = useState([]);
  const [selectedSubtopic, setSelectedSubtopic] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [actualData, setActualData] = useState(null);

  const [semanticAnalysis, setSemanticAnalysis] = useState(null);
  const [semanticLoading, setSemanticLoading] = useState(false);
  const [expandedGroups, setExpandedGroups] = useState(new Set());
  const [companyFilter, setCompanyFilter] = useState(null);
  const [allCompanies, setAllCompanies] = useState([]);
  const [documentViewer, setDocumentViewer] = useState({
    isOpen: false,
    documentId: null,
    verbatimProposals: [],
    subtopicName: null,
    semanticGroupName: null
  });

  const handleViewPDF = async (documentId, page, semanticGroupId, subtopicName) => {
    try {
      // Find verbatim proposals for this document from the current semantic groups
      const verbatimProposals = [];
      let currentSemanticGroupName = null;
      
      if (semanticAnalysis && selectedSubtopic) {
        const subtopicData = semanticAnalysis.subtopics.find(
          s => s.subtopic_name === selectedSubtopic.subtopic_name
        );
        
        if (subtopicData) {
          // If a specific semantic group is provided, only get proposals from that group
          if (semanticGroupId) {
            const targetGroup = subtopicData.semantic_groups.find(group => group.group_id === semanticGroupId);
            if (targetGroup) {
              currentSemanticGroupName = targetGroup.group_id?.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()) || 'Semantic Group';
              targetGroup.verbatim_proposals.forEach(proposal => {
                if (proposal.document === documentId) {
                  verbatimProposals.push(proposal);
                }
              });
            }
          } else {
            // Get proposals from all semantic groups for this document
            subtopicData.semantic_groups.forEach(group => {
              group.verbatim_proposals.forEach(proposal => {
                if (proposal.document === documentId) {
                  verbatimProposals.push(proposal);
                }
              });
            });
          }
        }
      }
      
      // Open the document viewer
      setDocumentViewer({
        isOpen: true,
        documentId: documentId,
        verbatimProposals: verbatimProposals,
        subtopicName: subtopicName || selectedSubtopic?.subtopic_name || selectedSubtopic?.name,
        semanticGroupName: currentSemanticGroupName
      });
      
    } catch (error) {
      console.error('Error accessing document:', error);
      toast.error('Error accessing document. Please try again.');
    }
  };

  const closeDocumentViewer = () => {
    setDocumentViewer({
      isOpen: false,
      documentId: null,
      verbatimProposals: [],
      subtopicName: null,
      semanticGroupName: null
    });
  };

  useEffect(() => {
    if (selectedMeeting && selectedAgenda) {
      loadSubtopics();
      loadSemanticAnalysis();
    }
  }, [selectedMeeting, selectedAgenda]);

  const handleDataLoaded = (data) => {
    setActualData(data);
  };



  const toggleGroupExpansion = (groupId) => {
    setExpandedGroups(prev => {
      const newExpanded = new Set(prev);
      if (newExpanded.has(groupId)) {
        newExpanded.delete(groupId);
      } else {
        newExpanded.add(groupId);
      }
      return newExpanded;
    });
  };

  // Handle company pill clicks - toggle filter on/off
  const handleCompanyClick = (company) => {
    if (companyFilter === company) {
      // If clicking the same company, clear the filter
      setCompanyFilter(null);
      toast.success('Company filter cleared');
    } else {
      // Set the new company filter
      setCompanyFilter(company);
      toast.success(`Filtering by ${company}`);
    }
  };

  // Reset actual data when subtopic changes and update company filter
  useEffect(() => {
    setActualData(null);
    setExpandedGroups(new Set()); // Clear expanded groups
    setCompanyFilter(null); // Reset company filter when subtopic changes
    
    // Update companies list to only include companies from the selected subtopic
    if (selectedSubtopic && semanticAnalysis) {
      const subtopicData = semanticAnalysis.subtopics?.find(
        s => s.subtopic_name === selectedSubtopic.subtopic_name || s.subtopic_name === selectedSubtopic.name
      );
      
      if (subtopicData) {
        const companies = new Set();
        subtopicData.semantic_groups?.forEach(group => {
          // Extract companies from verbatim proposals
          group.verbatim_proposals?.forEach(proposal => {
            if (proposal.company && proposal.company !== 'Unknown') {
              companies.add(proposal.company);
            }
          });
          // Extract companies from company_positions
          ['supporting', 'opposing', 'neutral'].forEach(stance => {
            group.company_positions?.[stance]?.forEach(pos => {
              if (pos.company && pos.company !== 'Unknown') {
                companies.add(pos.company);
              }
            });
          });
        });
        setAllCompanies(Array.from(companies).sort());
      } else {
        setAllCompanies([]);
      }
    } else {
      setAllCompanies([]);
    }
  }, [selectedSubtopic?.id, selectedSubtopic?.name, selectedSubtopic?.subtopic_name, semanticAnalysis]);

  const loadSemanticAnalysis = async () => {
    try {
      setSemanticLoading(true);
      console.log('🧠 Loading GPT-4o semantic analysis for', selectedMeeting, selectedAgenda);
      const response = await apiService.getSemanticAnalysis(selectedMeeting, selectedAgenda);
      const analysisData = response.data || {};
      console.log('📊 Semantic analysis loaded:', analysisData);
      
      setSemanticAnalysis(analysisData);
      // Note: Companies list is now set dynamically when a subtopic is selected
      
    } catch (error) {
      // If it's a 404, it might be an administrative agenda with no proposals
      if (error.response?.status === 404) {
        console.log('ℹ️ No semantic analysis found - checking if this is an administrative agenda');
        setSemanticAnalysis({ subtopics: [] }); // Set empty array
        setAllCompanies([]);
      } else {
        const errorMessage = handleApiError(error, 'Failed to load semantic analysis');
        console.error('❌ Semantic analysis error:', error);
        toast.error(errorMessage);
      }
    } finally {
      setSemanticLoading(false);
    }
  };

  const loadSubtopics = async () => {
    try {
      setLoading(true);
      setError(null);
      
      console.log('🔄 Loading subtopics for', selectedMeeting, selectedAgenda);
      const response = await apiService.getSubtopics(selectedMeeting, selectedAgenda);
      const subtopicsData = response.data || [];
      console.log('📊 Subtopics loaded:', subtopicsData.length, subtopicsData);
      
      // Sort subtopics: Overview first, then the rest
      const sortedSubtopics = subtopicsData.sort((a, b) => {
        const aIsOverview = (a.name || a.subtopic_name || '').includes('Overview');
        const bIsOverview = (b.name || b.subtopic_name || '').includes('Overview');
        
        if (aIsOverview && !bIsOverview) return -1;
        if (!aIsOverview && bIsOverview) return 1;
        return 0; // Maintain original order for non-overview subtopics
      });
      
      setSubtopics(sortedSubtopics);
      
      // Check if there's a selected subtopic from navigation state
      const targetSubtopic = location.state?.selectedSubtopic;
      if (targetSubtopic && sortedSubtopics.length > 0) {
        // Find the subtopic by name
        const found = sortedSubtopics.find(s => s.name === targetSubtopic);
        if (found) {
          setSelectedSubtopic(found);
          toast.success(`Navigated to subtopic: ${targetSubtopic}`);
        } else {
          // Fallback to first subtopic if not found
          setSelectedSubtopic(sortedSubtopics[0]);
        }
      } else if (sortedSubtopics.length > 0) {
        // Auto-select first subtopic if available and no navigation state
        setSelectedSubtopic(sortedSubtopics[0]);
      }
    } catch (error) {
      // If it's a 404, it might be an administrative agenda with no proposals
      if (error.response?.status === 404) {
        console.log('ℹ️ No subtopics found - this might be an administrative agenda');
        setSubtopics([]);
      } else {
        const errorMessage = handleApiError(error, 'Failed to load subtopics');
        setError(errorMessage);
        toast.error(errorMessage);
      }
    } finally {
      setLoading(false);
    }
  };



  if (loading) {
    return (
      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        <div className="lg:col-span-1">
          <div className="bg-white rounded-xl shadow-card p-6 animate-pulse">
            <div className="h-6 bg-gray-200 rounded mb-4"></div>
            <div className="h-10 bg-gray-200 rounded mb-4"></div>
            <div className="space-y-3">
              {[1, 2, 3].map((i) => (
                <div key={i} className="h-16 bg-gray-200 rounded"></div>
              ))}
            </div>
          </div>
        </div>
        <div className="lg:col-span-3">
          <div className="space-y-4">
            {[1, 2].map((i) => (
              <div key={i} className="card animate-pulse">
                <div className="h-20 bg-gray-200 rounded"></div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-12">
        <ExclamationTriangleIcon className="h-12 w-12 text-red-500 mx-auto mb-4" />
        <h3 className="text-lg font-medium text-charcoal-900 mb-2">Error Loading Subtopics</h3>
        <p className="text-charcoal-600 mb-4">{error}</p>
        <button onClick={loadSubtopics} className="btn-vodafone">
          Try Again
        </button>
      </div>
    );
  }

  // Check if this is an agenda without subtopics (administrative or CR-based)
  if (!loading && subtopics.length === 0) {
    return (
      <div className="space-y-6 animate-fadeIn">
        {/* Header */}
        <div>
          <h1 className="text-2xl font-bold text-charcoal-900">Subtopic Browser</h1>
          <p className="mt-2 text-charcoal-600">
            Deep dive into technical subtopics organized by semantic groups for Agenda {selectedAgenda} in {selectedMeeting}
          </p>
        </div>

        <div className="card text-center py-16">
          <div className="max-w-md mx-auto">
            <DocumentTextIcon className="h-16 w-16 text-gray-400 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-charcoal-900 mb-2">No Proposal Analysis</h3>
            <p className="text-charcoal-600 mb-4">
              This agenda item ({selectedAgenda}) does not contain technical proposals for semantic analysis. 
              It may contain specification changes (CRs), administrative documents (TRs, LSs, Work Items), 
              or other non-proposal content.
            </p>
            <p className="text-sm text-charcoal-500">
              To view a high-level summary and context, please check the <strong>Agenda Context</strong> page.
            </p>
          </div>
        </div>
      </div>
    );
  }

  // Generate and download PDF
  const generatePDF = () => {
    if (!selectedSubtopic || !semanticAnalysis) {
      toast.error('Please select a subtopic first');
      return;
    }

    try {
      toast.loading('Generating PDF...');

      // Create new PDF document
      const doc = new jsPDF();
      const pageWidth = doc.internal.pageSize.getWidth();
      const pageHeight = doc.internal.pageSize.getHeight();
      let yPosition = 20;

      // Vodafone red color
      const vodafoneRed = [230, 0, 0];

      // Add header with Vodafone branding
      doc.setFillColor(...vodafoneRed);
      doc.rect(0, 0, pageWidth, 15, 'F');
      doc.setTextColor(255, 255, 255);
      doc.setFontSize(14);
      doc.setFont(undefined, 'bold');
      doc.text('3GPP Semantic Analysis Report', 14, 10);

      // Reset text color
      doc.setTextColor(0, 0, 0);
      yPosition = 25;

      // Add meeting and agenda information
      doc.setFontSize(11);
      doc.setFont(undefined, 'bold');
      doc.text(`Meeting: ${selectedMeeting}`, 14, yPosition);
      yPosition += 6;
      doc.text(`Agenda: ${selectedAgenda}`, 14, yPosition);
      yPosition += 6;
      doc.text(`Subtopic: ${selectedSubtopic.subtopic_name || selectedSubtopic.name}`, 14, yPosition);
      yPosition += 10;

      // Add subtopic statistics
      doc.setFont(undefined, 'normal');
      doc.setFontSize(10);
      doc.text(`Total Proposals: ${selectedSubtopic.total_proposals || 0}`, 14, yPosition);
      yPosition += 5;
      doc.text(`Documents: ${selectedSubtopic.document_count || 0}`, 14, yPosition);
      yPosition += 5;
      if (selectedSubtopic.coverage_percentage) {
        doc.text(`Coverage: ${selectedSubtopic.coverage_percentage.toFixed(1)}%`, 14, yPosition);
        yPosition += 5;
      }
      yPosition += 5;

      // Find semantic groups for this subtopic
      const currentSubtopicData = semanticAnalysis.subtopics?.find(s => 
        s.subtopic_name === selectedSubtopic.subtopic_name || s.subtopic_name === selectedSubtopic.name
      );

      if (currentSubtopicData && currentSubtopicData.semantic_groups) {
        // Filter semantic groups by company if a filter is active
        let semanticGroups = currentSubtopicData.semantic_groups;
        
        if (companyFilter) {
          semanticGroups = semanticGroups.filter(group => {
            return group.verbatim_proposals?.some(proposal => proposal.company === companyFilter);
          });
        }

        // Add semantic groups section
        doc.setFontSize(12);
        doc.setFont(undefined, 'bold');
        doc.setTextColor(...vodafoneRed);
        doc.text('Semantic Groups', 14, yPosition);
        doc.setTextColor(0, 0, 0);
        yPosition += 8;

        // Process each semantic group
        semanticGroups.forEach((group, index) => {
          // Check if we need a new page
          if (yPosition > pageHeight - 40) {
            doc.addPage();
            yPosition = 20;
          }

          // Group header
          doc.setFontSize(10);
          doc.setFont(undefined, 'bold');
          const groupTitle = group.group_id?.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()) || `Group ${index + 1}`;
          doc.text(`${index + 1}. ${groupTitle}`, 14, yPosition);
          yPosition += 5;

          // Frequency badge
          doc.setFontSize(8);
          doc.setFont(undefined, 'normal');
          const freqText = `[${(group.frequency_category || '').toUpperCase()}]`;
          doc.setTextColor(100, 100, 100);
          doc.text(freqText, 14, yPosition);
          doc.setTextColor(0, 0, 0);
          yPosition += 6;

          // Technical concept
          doc.setFontSize(9);
          doc.setFont(undefined, 'italic');
          const conceptLines = doc.splitTextToSize(group.technical_concept || '', pageWidth - 28);
          doc.text(conceptLines, 14, yPosition);
          yPosition += conceptLines.length * 5 + 3;

          // Proposal count
          doc.setFont(undefined, 'normal');
          doc.setFontSize(8);
          doc.text(`${group.proposals_in_group || 0} proposals from ${group.document_count || 0} documents`, 14, yPosition);
          yPosition += 8;

          // Add proposals table
          if (group.verbatim_proposals && group.verbatim_proposals.length > 0) {
            let proposals = group.verbatim_proposals;
            
            // Filter by company if needed
            if (companyFilter) {
              proposals = proposals.filter(p => p.company === companyFilter);
            }

            if (proposals.length > 0) {
              const tableData = proposals.slice(0, 5).map(proposal => [
                proposal.document || 'N/A',
                proposal.company || 'Unknown',
                (proposal.stance || 'N/A').toUpperCase(),
                proposal.proposal_text || ''  // Full proposal text, no truncation
              ]);

              doc.autoTable({
                startY: yPosition,
                head: [['Document', 'Company', 'Stance', 'Proposal']],
                body: tableData,
                theme: 'striped',
                headStyles: {
                  fillColor: vodafoneRed,
                  textColor: [255, 255, 255],
                  fontStyle: 'bold',
                  fontSize: 8
                },
                bodyStyles: {
                  fontSize: 7,
                  cellPadding: 3,
                  valign: 'top'  // Align text to top for better readability
                },
                columnStyles: {
                  0: { cellWidth: 25 },
                  1: { cellWidth: 28 },
                  2: { cellWidth: 18 },
                  3: { cellWidth: 'auto', halign: 'left' }  // Auto-width for full text
                },
                margin: { left: 14, right: 14 },
                tableWidth: 'auto',
                styles: {
                  overflow: 'linebreak',  // Enable text wrapping
                  cellWidth: 'wrap'
                },
                didDrawPage: (data) => {
                  yPosition = data.cursor.y + 5;
                }
              });

              yPosition = doc.lastAutoTable.finalY + 10;

              if (proposals.length > 5) {
                doc.setFontSize(7);
                doc.setTextColor(100, 100, 100);
                doc.text(`... and ${proposals.length - 5} more proposals`, 14, yPosition);
                doc.setTextColor(0, 0, 0);
                yPosition += 8;
              }
            }
          }

          yPosition += 5;
        });

        // Add footer to last page
        const addFooter = () => {
          doc.setFontSize(8);
          doc.setTextColor(100, 100, 100);
          doc.text(
            `Generated on ${new Date().toLocaleString()}`,
            14,
            pageHeight - 10
          );
          doc.text(
            `Page ${doc.internal.getNumberOfPages()}`,
            pageWidth - 30,
            pageHeight - 10
          );
        };

        // Add footer to all pages
        const totalPages = doc.internal.getNumberOfPages();
        for (let i = 1; i <= totalPages; i++) {
          doc.setPage(i);
          addFooter();
        }
      }

      // Save the PDF
      const filename = `${selectedMeeting.replace('#', '')}_${selectedAgenda.replace('.', '_')}_${selectedSubtopic.subtopic_name?.replace(/[^a-zA-Z0-9]/g, '_') || 'subtopic'}.pdf`;
      doc.save(filename);

      toast.dismiss();
      toast.success('PDF downloaded successfully!');
    } catch (error) {
      toast.dismiss();
      toast.error('Failed to generate PDF');
      console.error('PDF generation error:', error);
    }
  };

  return (
    <div className="space-y-6 animate-fadeIn">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-2xl font-bold text-charcoal-900">Subtopic Browser</h1>
          <p className="mt-2 text-charcoal-600">
            Deep dive into technical subtopics organized by semantic groups for Agenda {selectedAgenda} in {selectedMeeting}
          </p>
        </div>
        
        {/* Download Button */}
        {selectedSubtopic && (
          <button
            onClick={generatePDF}
            className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-vodafone-600 hover:bg-vodafone-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-vodafone-500 transition-colors"
            title="Download PDF Report"
          >
            <ArrowDownTrayIcon className="h-5 w-5 mr-2" />
            Download PDF
          </button>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-6 gap-6">
        {/* Left Sidebar - Subtopics */}
        <div className="lg:col-span-1">
          <SubtopicSidebar
            subtopics={subtopics}
            selectedSubtopic={selectedSubtopic}
            onSelectSubtopic={setSelectedSubtopic}
            loading={loading}
          />
        </div>

        {/* Main Panel */}
        <div className="lg:col-span-3">
          {selectedSubtopic ? (
            <div className="space-y-6">

              {/* Semantic Groups Content */}
              {semanticLoading ? (
                <div className="card text-center py-12">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-vodafone-500 mx-auto mb-4"></div>
                  <p className="text-gray-600">Loading GPT-4o semantic analysis...</p>
                </div>
              ) : selectedSubtopic && semanticAnalysis ? (
                (() => {
                  // Find the semantic data for the selected subtopic by exact name match
                  // Since both APIs now use the same data source, names should match exactly
                  const currentSubtopicData = semanticAnalysis.subtopics?.find(s => 
                    s.subtopic_name === selectedSubtopic.subtopic_name || s.subtopic_name === selectedSubtopic.name
                  );
                  
                  // Extract all unique companies from all semantic groups in this subtopic
                  const getAllSubtopicCompanies = (subtopicData) => {
                    const companies = new Set();
                    subtopicData?.semantic_groups?.forEach(group => {
                      group.verbatim_proposals?.forEach(proposal => {
                        if (proposal.company) {
                          companies.add(proposal.company);
                        }
                      });
                    });
                    return Array.from(companies).sort();
                  };

                  const allSubtopicCompanies = currentSubtopicData ? getAllSubtopicCompanies(currentSubtopicData) : [];
                  
                  if (!currentSubtopicData || !currentSubtopicData.semantic_groups) {
                    return (
                      <div className="card text-center py-12">
                        <BeakerIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                        <h3 className="text-lg font-medium text-gray-900 mb-2">No Semantic Groups Found</h3>
                        <p className="text-gray-600">No semantic analysis available for this subtopic.</p>
                      </div>
                    );
                  }
                  
                  // Filter semantic groups by company if filter is active
                  const allSemanticGroups = currentSubtopicData.semantic_groups;
                  const semanticGroups = companyFilter 
                    ? allSemanticGroups.filter(group => {
                        // Check if company appears in verbatim proposals
                        const hasCompany = group.verbatim_proposals?.some(p => p.company === companyFilter);
                        if (hasCompany) return true;
                        
                        // Check if company appears in company_positions
                        return ['supporting', 'opposing', 'neutral'].some(stance =>
                          group.company_positions?.[stance]?.some(pos => pos.company === companyFilter)
                        );
                      })
                    : allSemanticGroups;
                  
                  const commonGroups = semanticGroups.filter(g => g.frequency_category === 'common');
                  const intermediateGroups = semanticGroups.filter(g => g.frequency_category === 'intermediate');
                  const rareGroups = semanticGroups.filter(g => g.frequency_category === 'rare');
                  
                  return (
                    <div className="space-y-6">
                      {/* Company Filter */}
                      {allCompanies.length > 0 && (
                        <div className="card">
                          <div className="flex items-center gap-3">
                            <FunnelIcon className="h-5 w-5 text-gray-500 flex-shrink-0" />
                            <div className="flex-1">
                              <label className="block text-sm font-medium text-gray-700 mb-1">
                                Filter by Company
                              </label>
                              <select
                                value={companyFilter || ''}
                                onChange={(e) => setCompanyFilter(e.target.value || null)}
                                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-vodafone-500 focus:border-vodafone-500"
                              >
                                <option value="">All Companies ({allSemanticGroups.length} clusters)</option>
                                {allCompanies.map(company => (
                                  <option key={company} value={company}>{company}</option>
                                ))}
                              </select>
                            </div>
                            {companyFilter && (
                              <button
                                onClick={() => setCompanyFilter(null)}
                                className="px-3 py-2 text-sm bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
                              >
                                Clear
                              </button>
                            )}
                          </div>
                          {companyFilter && (
                            <div className="mt-3 bg-vodafone-50 border-l-4 border-vodafone-500 p-3 rounded-r">
                              <p className="text-sm text-vodafone-900">
                                <strong>{companyFilter}</strong> appears in <strong>{semanticGroups.length}</strong> of {allSemanticGroups.length} semantic clusters
                              </p>
                            </div>
                          )}
                        </div>
                      )}
                      
                      {/* Combined Summary Header */}
                      <div className="card">
                        <div className="flex items-center justify-between mb-4">
                          <div className="flex-1">
                            <div className="mb-3">
                              <h2 className="text-xl font-bold text-charcoal-900 mb-2">
                                {currentSubtopicData.subtopic_name}
                              </h2>
                              {allSubtopicCompanies.length > 0 && (
                                <div className="flex flex-wrap gap-1">
                                  {allSubtopicCompanies.map((company, index) => (
                                    <CompanyPill 
                                      key={index} 
                                      company={company}
                                      highlighted={companyFilter && company === companyFilter}
                                      onClick={handleCompanyClick}
                                    />
                                  ))}
                                </div>
                              )}
                            </div>
                            <div className="text-sm text-gray-600">
                              {currentSubtopicData.total_proposals} proposals • {currentSubtopicData.document_count} documents • {semanticGroups.length} semantic groups
                              {allSubtopicCompanies.length > 0 && (
                                <span> • {allSubtopicCompanies.length} contributing companies</span>
                              )}
                            </div>
                          </div>

                        </div>
                        
                        <div className="grid grid-cols-3 gap-4 mb-3">
                          <div className="text-center">
                            <div className="text-2xl font-bold text-green-700 mb-1">{commonGroups.length}</div>
                            <div className="text-sm font-medium text-green-900">Common</div>
                            <div className="text-xs text-gray-500">≥50% of documents</div>
                          </div>
                          <div className="text-center">
                                                    <div className="text-2xl font-bold text-orange-700 mb-1">{intermediateGroups.length}</div>
                        <div className="text-sm font-medium text-orange-900">Intermediate</div>
                            <div className="text-xs text-gray-500">20-49% of documents</div>
                          </div>
                          <div className="text-center">
                            <div className="text-2xl font-bold text-purple-700 mb-1">{rareGroups.length}</div>
                            <div className="text-sm font-medium text-purple-900">Rare</div>
                            <div className="text-xs text-gray-500">&lt;20% of documents</div>
                          </div>
                        </div>
                      </div>

                      {/* Semantic Groups */}
                      <div className="space-y-4">
                        {commonGroups.length > 0 && (
                          <div className="space-y-3">
                            {commonGroups.map((group) => (
                              <SemanticGroupCard
                                key={group.group_id}
                                group={group}
                                isExpanded={expandedGroups.has(group.group_id)}
                                onToggle={() => toggleGroupExpansion(group.group_id)}
                                handleViewPDF={handleViewPDF}
                                subtopicName={selectedSubtopic?.subtopic_name || selectedSubtopic?.name}
                                companyFilter={companyFilter}
                                onCompanyClick={handleCompanyClick}
                              />
                            ))}
                          </div>
                        )}
                        
                        {intermediateGroups.length > 0 && (
                          <div className="space-y-3">
                                                          {intermediateGroups.map((group) => (
                              <SemanticGroupCard
                                key={group.group_id}
                                group={group}
                                isExpanded={expandedGroups.has(group.group_id)}
                                onToggle={() => toggleGroupExpansion(group.group_id)}
                                handleViewPDF={handleViewPDF}
                                subtopicName={selectedSubtopic?.subtopic_name || selectedSubtopic?.name}
                                companyFilter={companyFilter}
                                onCompanyClick={handleCompanyClick}
                              />
                            ))}
                          </div>
                        )}
                        
                        {rareGroups.length > 0 && (
                          <div className="space-y-3">
                            {rareGroups.map((group) => (
                              <SemanticGroupCard
                                key={group.group_id}
                                group={group}
                                isExpanded={expandedGroups.has(group.group_id)}
                                onToggle={() => toggleGroupExpansion(group.group_id)}
                                handleViewPDF={handleViewPDF}
                                subtopicName={selectedSubtopic?.subtopic_name || selectedSubtopic?.name}
                                companyFilter={companyFilter}
                                onCompanyClick={handleCompanyClick}
                              />
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })()
              ) : (
                <div className="card text-center py-12">
                  <BeakerIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-gray-900 mb-2">Select a Subtopic</h3>
                  <p className="text-gray-600">Choose a subtopic from the sidebar to view its semantic analysis.</p>
                </div>
              )}
            </div>
          ) : (
            <div className="card text-center py-12">
              <TagIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-charcoal-900 mb-2">Select a Subtopic</h3>
              <p className="text-charcoal-600">
                Choose a subtopic from the sidebar to view its proposals and details.
              </p>
            </div>
          )}
        </div>

        {/* Right Sidebar - Executive Summary */}
        <div className="lg:col-span-2">
          <StrategicBriefSidebar
            selectedSubtopic={selectedSubtopic}
          />
        </div>
      </div>

      {/* Document Viewer Modal */}
      <PDFDocumentViewer
        documentId={documentViewer.documentId}
        isOpen={documentViewer.isOpen}
        onClose={closeDocumentViewer}
        verbatimProposals={documentViewer.verbatimProposals}
        subtopicName={documentViewer.subtopicName}
        semanticGroupName={documentViewer.semanticGroupName}
      />

    </div>
  );
};

export default SubtopicBrowser; 